/**
 * セキュリティ設定ページ（PRODUCT_DEV-396）
 *
 * パスキー（生体認証）の登録/削除UI。
 * Unizo 等の各プロダクトから returnUrl 付きで遷移してくる。
 *
 * 表示モード:
 *   - tenant.passkeyEnabled === false → 「管理者がパスキーを許可していません」
 *   - tenant.passkeyEnabled === true  → パスキー一覧 + 登録/削除UI
 */
import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowLeft, Fingerprint, Trash2 } from "lucide-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { AuthPageLayout } from "@/pages/LoginPage/components/AuthPageLayout";
import { LoadingButton } from "@/pages/LoginPage/components/LoadingButton";
import { PortalAuthClientWithSelfUI } from "@/lib/portalAuthClient";
import { translateCognitoMessage } from "@/utils/cognitoErrorMessages";
import { isPlatformAuthenticatorAvailable } from "@/utils/webauthnSupport";
import type { WhoamiMembership } from "@shared-domain/auth-service/contract";

interface PasskeyItem {
  credentialId: string;
  friendlyName?: string;
}

export const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "";

  const client = useMemo(() => new PortalAuthClientWithSelfUI(), []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [currentMembership, setCurrentMembership] =
    useState<WhoamiMembership | null>(null);
  const [tenantPasskeyEnabled, setTenantPasskeyEnabled] =
    useState<boolean>(false);
  const [passkeys, setPasskeys] = useState<PasskeyItem[]>([]);
  // Platform Authenticator (Windows Hello / Touch ID 等) が無い端末では
  // associateWebAuthnCredential() が QR コードでクロスデバイス認証を出すため
  // 登録ボタンを無効化する。初期値は true (チェック完了まで暫定的に有効)。
  const [platformAuthAvailable, setPlatformAuthAvailable] =
    useState<boolean>(true);

  // セッションと whoami を取得
  const initialize = useCallback(async () => {
    try {
      // セッション確認
      // forceRefresh: true で Amplify 内部の token を必ずリフレッシュする
      // (cached expired token のまま listWebAuthnCredentials が呼ばれて
      //  「Access Token has been revoked」で silent fail し、
      //  パスキー一覧が空表示になる事象を防ぐため)
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      const accessToken = session.tokens?.accessToken?.toString();
      if (!idToken || !accessToken) {
        navigate(`/?returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }
      await client.setTokens(idToken, accessToken);

      // ユーザ情報取得
      await client.whoami();
      const me = client.me;
      if (!me) {
        setError("ユーザー情報の取得に失敗しました");
        return;
      }

      // 現在のテナントを特定
      const currentTid =
        me.currentTenantId ?? me.memberships[0]?.tenantId ?? "";
      const membership = me.memberships.find(
        (m) => m.tenantId === currentTid,
      );

      setCurrentMembership(membership ?? null);
      setTenantPasskeyEnabled(Boolean(membership?.tenantPasskeyEnabled));

      // Platform Authenticator 有無を判定 (QR 経路を防ぐ事前チェック)
      setPlatformAuthAvailable(await isPlatformAuthenticatorAvailable());

      // 既に登録されているパスキー一覧を取得
      // 失敗時はユーザに伝わるエラーを表示する (silent fail で空表示にしない)
      try {
        const list = await client.listPasskeys();
        setPasskeys(list);
      } catch (err) {
        console.error("Failed to load passkeys:", err);
        const e = err as { message?: string };
        setError(
          translateCognitoMessage(e.message) ??
            "認証セッションの有効期限が切れた可能性があります。お手数ですが再ログインしてください。",
        );
      }
    } catch (err) {
      console.error("SecurityPage init error:", err);
      setError("セキュリティ設定の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [client, navigate, returnUrl]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleBack = () => {
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      navigate("/");
    }
  };

  // パスキー登録
  const handleRegister = async () => {
    setError(null);
    setMessage(null);
    setSaving(true);
    try {
      const result = await client.registerPasskey();
      const list = await client.listPasskeys();
      setPasskeys(list);
      setMessage(
        result === "alreadyOnThisDevice"
          ? "この端末にはすでにパスキーがあります。次回からこのブラウザでパスキーを使ってログインできます。"
          : "パスキーを登録しました。",
      );
    } catch (err) {
      const e = err as { message?: string };
      setError(
        translateCognitoMessage(e.message) ??
          e.message ??
          "パスキーの登録に失敗しました",
      );
    } finally {
      setSaving(false);
    }
  };

  // パスキー削除
  const handleDelete = async (credentialId: string) => {
    setError(null);
    setMessage(null);
    setSaving(true);
    try {
      await client.deletePasskey(credentialId);
      const list = await client.listPasskeys();
      setPasskeys(list);
      setMessage("パスキーを削除しました。");
    } catch (err) {
      const e = err as { message?: string };
      setError(
        translateCognitoMessage(e.message) ??
          e.message ??
          "パスキーの削除に失敗しました",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AuthPageLayout>
        <p className="text-center text-neutral-600">読み込み中...</p>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <h2 className="text-2xl font-bold text-center text-neutral-900 mb-2">
        パスキー設定
      </h2>
      <p className="text-sm text-center text-neutral-600 mb-6">
        指紋認証や顔認証でログインできるパスキーを管理します。
      </p>

      {/* テナント側で許可されていない場合 */}
      {!tenantPasskeyEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800">
            管理者がこのテナントでパスキーを許可していません。利用するには管理者にお問い合わせください。
          </p>
        </div>
      )}

      {/* テナント側ON: パスキーの登録/削除UI */}
      {tenantPasskeyEnabled && (
        <>
          {passkeys.length > 0 && (
            <div className="mb-6 space-y-3">
              <h3 className="text-sm font-medium text-neutral-700">
                登録済みパスキー
              </h3>
              {passkeys.map((pk) => (
                <div
                  key={pk.credentialId}
                  className="flex items-center justify-between p-3 bg-neutral-50 border border-neutral-200 rounded-xl"
                >
                  <div className="flex items-center gap-2">
                    <Fingerprint className="w-5 h-5 text-neutral-500" />
                    <span className="text-sm text-neutral-800">
                      {pk.friendlyName ||
                        `パスキー (${pk.credentialId.slice(0, 8)}...)`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(pk.credentialId)}
                    className="text-red-500 hover:text-red-700 p-1"
                    disabled={saving}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!platformAuthAvailable && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                この端末には生体認証（Windows Hello / Touch ID 等）が設定されていないため、パスキーを登録できません。端末の生体認証を設定してから再度お試しください。
              </p>
            </div>
          )}

          <LoadingButton
            type="button"
            loading={saving}
            loadingMessage="処理中..."
            label="パスキーを登録する"
            onClick={handleRegister}
            icon={<Fingerprint className="w-6 h-6" />}
            disabled={!platformAuthAvailable || saving}
          />
        </>
      )}

      {/* メッセージ */}
      {message && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
          {message}
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
          {error}
        </div>
      )}

      {/* 戻るボタン */}
      <button
        type="button"
        onClick={handleBack}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-3 mt-6 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
      >
        <ArrowLeft size={16} />
        基本設定に戻る
      </button>

      {/* 現在の所属情報を補助表示 */}
      {currentMembership && (
        <p className="text-xs text-neutral-400 text-center mt-4">
          現在のテナント: {currentMembership.tenantName}
        </p>
      )}
    </AuthPageLayout>
  );
};

export default SecurityPage;
